/**
 * Столбцы доски и их порядок.
 *
 * §9.5: порядок меняется ТОЛЬКО здесь. На канбане столбцы не перетаскиваются —
 * там перетаскиваются задачи, и смешивать эти жесты нельзя.
 *
 * §8.3: в режиме COLUMN у каждого активного столбца обязан быть проект.
 * Столбец без проекта в этом режиме — нарушение инварианта §20.3, поэтому
 * он помечается явно — и здесь же исправляется правкой столбца.
 *
 * Прежде у «Добавить столбец» не было обработчика, правки столбца не было
 * вовсе, а удаление показывало «подключается вместе с backend».
 */

import { Permission, ProjectBindingMode } from "@taskflow/contracts";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { usePermission, type WsError } from "@taskflow/client-core";
import {
  Button,
  Checkbox,
  FormField,
  IconButton,
  Input,
  Modal,
  type PickerOption,
  Select,
  SortableList,
  Tag,
  toast,
} from "@/shared/ui";
import { Pencil, Plus, Trash2 } from "@/shared/ui/motionIcons";

import { useProjectsQuery } from "@taskflow/client-core/admin";
import {
  type BoardColumn,
  type BoardDetail,
  useCreateColumnMutation,
  useDeleteColumnMutation,
  useReorderColumnsMutation,
  useUpdateColumnMutation,
} from "@taskflow/client-core/boards";
import { STATUS_CATEGORIES } from "../statusCategories";

export function ColumnsSection({
  board,
  permissions,
}: {
  board: BoardDetail;
  permissions: readonly Permission[];
}) {
  const canEdit = usePermission(Permission.BOARD_COLUMNS, permissions);
  const canReorder = usePermission(Permission.BOARD_COLUMNS_REORDER, permissions);
  const [reorder] = useReorderColumnsMutation();

  const active = board.columns.filter((column) => column.isActive);
  const [order, setOrder] = useState<BoardColumn[]>(active);
  const [deleting, setDeleting] = useState<BoardColumn | null>(null);
  const [editing, setEditing] = useState<BoardColumn | "new" | null>(null);

  // Порядок с сервера — источник истины; локальное состояние нужно только
  // для мгновенного отклика при перетаскивании. Синхронизация во время
  // рендера, а не в эффекте: эффект дал бы лишний проход отрисовки.
  const [syncedFrom, setSyncedFrom] = useState(board.columns);
  if (syncedFrom !== board.columns) {
    setSyncedFrom(board.columns);
    setOrder(active);
  }

  const applyOrder = (ids: string[]) => {
    const next = ids
      .map((id) => order.find((column) => column.id === id))
      .filter((column): column is BoardColumn => column !== undefined);
    setOrder(next);

    void reorder({ boardId: board.id, columnIds: ids })
      .unwrap()
      .catch((raw: unknown) => {
        // Откат: без него список останется в порядке, которого нет на сервере.
        setOrder(active);
        toast.error(raw as WsError, "Не удалось изменить порядок столбцов");
      });
  };

  const columnMode = board.projectBindingMode === ProjectBindingMode.COLUMN;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[13px] font-extrabold text-fg">Столбцы</h2>
          <p className="text-[11px] text-faint">
            {canReorder
              ? "Перетащите столбец или используйте клавиатуру, чтобы изменить порядок"
              : "Для изменения порядка нужно отдельное право"}
          </p>
        </div>
        {canEdit && (
          <Button size="sm" variant="secondary" icon={Plus} onClick={() => setEditing("new")}>
            Добавить столбец
          </Button>
        )}
      </div>

      <SortableList
        label="Столбцы доски"
        items={order}
        itemKey={(column) => column.id}
        disabled={!canReorder}
        onReorder={applyOrder}
        renderItem={(column) => (
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-fg">{column.name}</p>
              <p className="flex items-center gap-1.5 truncate text-[11px] text-faint">
                {columnMode ? (
                  column.projectName === null ? (
                    <span className="flex items-center gap-1 font-semibold text-danger">
                      <AlertTriangle className="size-3" aria-hidden />
                      Проект не задан
                    </span>
                  ) : (
                    <>Проект: {column.projectName}</>
                  )
                ) : (
                  <>
                    Статус:{" "}
                    {STATUS_CATEGORIES.find((item) => item.id === column.statusCategory)?.label ??
                      "не задан"}
                  </>
                )}
                {column.requireAssigneeOnEntry && <> · требует исполнителя</>}
              </p>
            </div>

            {column.allowedTargets.length === 0 && (
              <Tag tone="neutral" size="sm">
                конечный
              </Tag>
            )}

            {canEdit && (
              <>
                <IconButton
                  icon={Pencil}
                  label={`Изменить столбец ${column.name}`}
                  size="sm"
                  onClick={() => setEditing(column)}
                />
                <IconButton
                  icon={Trash2}
                  label={`Удалить столбец ${column.name}`}
                  size="sm"
                  variant="danger"
                  disabled={order.length <= 1}
                  title={order.length <= 1 ? "Последний столбец удалить нельзя" : undefined}
                  onClick={() => setDeleting(column)}
                />
              </>
            )}
          </div>
        )}
      />

      {editing !== null && (
        <ColumnDialog
          board={board}
          column={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting !== null && (
        <DeleteColumnDialog
          board={board}
          column={deleting}
          others={order.filter((column) => column.id !== deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/** Открывается и из меню столбца на канбане: «Изменить проект столбца». */
export function ColumnDialog({
  board,
  column,
  onClose,
}: {
  board: BoardDetail;
  column: BoardColumn | null;
  onClose: () => void;
}) {
  const columnMode = board.projectBindingMode === ProjectBindingMode.COLUMN;
  const projects = useProjectsQuery({}, { skip: !columnMode });
  const [createColumn, { isLoading: creating }] = useCreateColumnMutation();
  const [updateColumn, { isLoading: updating }] = useUpdateColumnMutation();

  const [name, setName] = useState(column?.name ?? "");
  const [projectId, setProjectId] = useState<string | null>(column?.projectId ?? null);
  const [statusCategory, setStatusCategory] = useState<string | null>(
    column?.statusCategory ?? "OPEN",
  );
  const [requireAssignee, setRequireAssignee] = useState(column?.requireAssigneeOnEntry ?? false);
  const [error, setError] = useState<string | null>(null);
  const busy = creating || updating;

  const projectOptions: PickerOption[] = (projects.data?.data ?? [])
    .filter((item) => item.status === "ACTIVE")
    .map((item) => ({ id: item.id, label: item.name }));

  const submit = async () => {
    if (name.trim() === "") {
      setError("Укажите название столбца");
      return;
    }
    if (!columnMode && statusCategory === null) {
      setError("Выберите статус столбца");
      return;
    }
    // §20.3: в режиме COLUMN столбец без проекта недопустим.
    if (columnMode && projectId === null) {
      setError("В режиме «проект для каждого столбца» у столбца обязателен проект");
      return;
    }
    setError(null);
    try {
      if (column === null) {
        await createColumn({
          boardId: board.id,
          name: name.trim(),
          projectId: columnMode ? projectId : null,
          statusCategory: columnMode ? null : statusCategory,
          requireAssigneeOnEntry: requireAssignee,
        }).unwrap();
        toast.success("Столбец добавлен", name.trim());
      } else {
        await updateColumn({
          boardId: board.id,
          columnId: column.id,
          version: column.version,
          name: name.trim(),
          ...(columnMode ? { projectId } : {}),
          // Только если статус действительно сменили: смена пишет событие
          // в историю каждой задачи столбца.
          ...(!columnMode && statusCategory !== null && statusCategory !== column.statusCategory
            ? { statusCategory }
            : {}),
          requireAssigneeOnEntry: requireAssignee,
        }).unwrap();
        toast.success("Столбец изменён", name.trim());
      }
      onClose();
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={column === null ? "Новый столбец" : "Изменить столбец"}
      width="sm"
      dismissable={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {column === null ? "Добавить" : "Сохранить"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Название" required>
          {(props) => (
            <Input {...props} value={name} autoFocus onChange={(event) => setName(event.target.value)} />
          )}
        </FormField>

        {columnMode ? (
          <FormField label="Проект столбца" required>
            {(props) => (
              <Select
                {...props}
                label="Проект столбца"
                placeholder="Выберите проект"
                options={projectOptions}
                value={projectId}
                onChange={setProjectId}
              />
            )}
          </FormField>
        ) : (
          <FormField
            label="Статус для отчётности"
            required
            hint={
              column !== null && statusCategory !== column.statusCategory
                ? "Статус изменится у всех задач этого столбца — это попадёт в их историю и отчёты"
                : "По нему считаются показатели (§17.4)"
            }
          >
            {(props) => (
              <Select
                {...props}
                label="Статус для отчётности"
                placeholder="Выберите статус"
                options={STATUS_CATEGORIES}
                value={statusCategory}
                onChange={setStatusCategory}
              />
            )}
          </FormField>
        )}

        <Checkbox
          label="При переносе в столбец требуется исполнитель"
          checked={requireAssignee}
          onChange={setRequireAssignee}
        />

        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** §21.6: удаление требует столбца, куда перенести задачи, — они не пропадают. */
function DeleteColumnDialog({
  board,
  column,
  others,
  onClose,
}: {
  board: BoardDetail;
  column: BoardColumn;
  others: BoardColumn[];
  onClose: () => void;
}) {
  const [deleteColumn, { isLoading }] = useDeleteColumnMutation();
  const [targetId, setTargetId] = useState<string | null>(others[0]?.id ?? null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (targetId === null) {
      setError("Выберите столбец, куда перенести задачи");
      return;
    }
    setError(null);
    try {
      await deleteColumn({ boardId: board.id, columnId: column.id, targetColumnId: targetId }).unwrap();
      toast.success("Столбец удалён", column.name);
      onClose();
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Удалить столбец"
      width="sm"
      dismissable={!isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button variant="danger" loading={isLoading} onClick={() => void submit()}>
            Удалить столбец
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          Столбец «{column.name}» будет удалён. Задачи не удаляются — они переедут в выбранный
          столбец.
        </p>
        <FormField label="Куда перенести задачи" required>
          {(props) => (
            <Select
              {...props}
              label="Куда перенести задачи"
              placeholder="Выберите столбец"
              options={others.map((item) => ({ id: item.id, label: item.name }))}
              value={targetId}
              onChange={setTargetId}
            />
          )}
        </FormField>
        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
